import React, { useMemo, useState } from "react";
import { Card, Button } from 'react-bootstrap';
import { FaLink } from "react-icons/fa";
import { formataDataHoraExibicao, situacoesAgendamento, statusExame, statusExamesSelecionados } from '../../../common/utils';
import { tokenStatus, semData as SEM_DATA, ESTILO_ROTULO_FILTRO, ESTILO_INPUT_FILTRO, ESTILO_SELECT_FILTRO } from './tokens';

// Elo da corrente exame → agendamento → serviço. A lacuna é tracejada em âmbar:
// o olho lê pela forma antes de ler o texto.
const Elo = ({ texto, estado }) => {
    const base = {
        display: 'inline-flex', alignItems: 'center', gap: '6px',
        borderRadius: '5px', padding: '4px 9px', fontSize: '12px', whiteSpace: 'nowrap',
    };

    if (estado === 'lacuna') {
        return <span style={{ ...base, border: '1px dashed #d9c48a', background: '#fdf8ec', color: '#8a6a1f', fontWeight: 600 }}>{texto}</span>;
    }
    if (estado === 'vazio') {
        return <span style={{ fontSize: '12px', color: '#b9c4d1' }}>{texto}</span>;
    }

    const destaque = estado === 'atual';
    return (
        <span style={{
            ...base,
            border: destaque ? '1px solid #c9d9f5' : '1px solid #dbe3ec',
            background: destaque ? '#eef4ff' : '#f7fafc',
            color: destaque ? '#1c4a94' : '#26364a',
            fontWeight: destaque ? 600 : 400,
        }}>
            <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: destaque ? '#4c6ef5' : '#1f9d55' }}></span>
            {texto}
        </span>
    );
};

// 1 linha = 1 ciclo (exame → agendamento → serviço), não 1 agendamento: junta os
// agendamentos com os exames solicitados que ainda não geraram agendamento.
export const montarCiclos = (agendamentos = [], exames = []) => {
    const ciclos = [];

    // mapa exame solicitado -> pedido, para o agrupamento e o painel de detalhe
    const pedidoDoExame = new Map();
    exames.forEach(pedido => {
        (pedido.exames_solicitados || []).forEach(ex => pedidoDoExame.set(String(ex.id), pedido));
    });

    agendamentos.forEach(ag => {
        const servicoRel = ag.dados_servico_relacionado || null;
        const cancelado = ag.situacao === 'cancelado';
        const finalizado = ag.situacao === 'finalizado';

        let lacuna = null;
        if (!cancelado) {
            if (!ag.exame_solicitado) lacuna = 'sem_exame';
            else if (finalizado && !servicoRel) lacuna = 'finalizado_sem_servico';
        }

        const pedido = ag.exame_solicitado ? pedidoDoExame.get(String(ag.exame_solicitado)) : null;

        ciclos.push({
            key: `ag-${ag.id}`,
            tipo: 'agendamento',
            rotulo: `#${ag.id}`,
            servico_nome: ag.dados_servico_procedimento?.nome || '-',
            exame_id: ag.exame_solicitado || null,
            agendamento_id: ag.id,
            servico_rel: servicoRel,
            data: ag.data_agendamento,
            data_cancelamento: ag.data_cancelamento,
            prestador_nome: ag.dados_prestador?.nome || null,
            situacao: ag.situacao,
            cancelado,
            lacuna,
            pedido_id: pedido ? pedido.id : null,
            pedido,
            agendamento: ag,
        });
    });

    const exameIdsComAgendamento = new Set(
        agendamentos.filter(a => a.exame_solicitado).map(a => String(a.exame_solicitado))
    );

    exames.forEach(pedido => {
        (pedido.exames_solicitados || []).forEach(ex => {
            if (exameIdsComAgendamento.has(String(ex.id))) return;
            if (ex.dados_agendamento && ex.dados_agendamento.id) return;
            if (['cancelado', 'recusado'].includes((ex.status || '').toLowerCase())) return;

            ciclos.push({
                key: `ex-${ex.id}`,
                tipo: 'exame',
                rotulo: `#${ex.id}`,
                servico_nome: ex.dados_servico_procedimento?.nome || '-',
                exame_id: ex.id,
                agendamento_id: null,
                servico_rel: null,
                data: null,
                prestador_nome: ex.dados_laboratorio?.nome || null,
                situacao: ex.status || 'solicitado',
                cancelado: false,
                lacuna: 'exame_sem_agendamento',
                pedido_id: pedido.id,
                pedido,
                exame: ex,
            });
        });
    });

    return ciclos;
};

const GRADE = '112px 1fr 210px 150px 168px';


const CicloAtendimento = ({
    atendimentoId,
    agendamentos = [],
    exames = [],
    dadosUsuario = {},
    podeAgendar = false,
    podeAbrirPedidoExame = false,
    podeVincularExame = () => false,
    onVincularExame,
    onGerarServico,
    onConfirmarRealizacao,
    onEditarAgendamento,
    onAgendarServico,
    onNovoPedidoExame,
    totalAgendamentos = 0,
}) => {
    const [filtro, setFiltro] = useState('todos');
    const [busca, setBusca] = useState('');
    const [agrupado, setAgrupado] = useState(false);
    const [cicloKey, setCicloKey] = useState(null);
    const [fStatus, setFStatus] = useState('');
    const [fServico, setFServico] = useState('');
    const [fPrestador, setFPrestador] = useState('');
    const [fDataDe, setFDataDe] = useState('');
    const [fDataAte, setFDataAte] = useState('');

    const ciclos = useMemo(() => montarCiclos(agendamentos, exames), [agendamentos, exames]);

    // opções dos filtros saem do que existe nos ciclos, não de tabelas fixas
    const opcoes = useMemo(() => {
        const unico = (arr) => [...new Set(arr.filter(Boolean))].sort();
        return {
            status: unico(ciclos.map(c => c.situacao)),
            servicos: unico(ciclos.map(c => c.servico_nome)),
            prestadores: unico(ciclos.map(c => c.prestador_nome)),
        };
    }, [ciclos]);

    const rotuloSituacao = (c) => (c.tipo === 'exame'
        ? (statusExamesSelecionados[c.situacao] || statusExame[c.situacao])
        : situacoesAgendamento[c.situacao]) || String(c.situacao || '').replace(/_/g, ' ');

    const contar = (tipo) => ciclos.filter(c => c.lacuna === tipo).length;
    const filtros = [
        { chave: 'todos', rotulo: 'Todos', total: ciclos.length },
        { chave: 'sem_exame', rotulo: 'Sem exame vinculado', total: contar('sem_exame') },
        { chave: 'exame_sem_agendamento', rotulo: 'Exame sem agendamento', total: contar('exame_sem_agendamento') },
        { chave: 'finalizado_sem_servico', rotulo: 'Finalizado sem serviço', total: contar('finalizado_sem_servico') },
    ];

    const termo = busca.trim().toLowerCase();
    const visiveis = ciclos.filter(c => {
        if (filtro !== 'todos' && c.lacuna !== filtro) return false;
        if (fStatus && c.situacao !== fStatus) return false;
        if (fServico && c.servico_nome !== fServico) return false;
        if (fPrestador && c.prestador_nome !== fPrestador) return false;

        if (fDataDe || fDataAte) {
            if (SEM_DATA(c.data)) return false;
            const dia = String(c.data).slice(0, 10);
            if (fDataDe && dia < fDataDe) return false;
            if (fDataAte && dia > fDataAte) return false;
        }

        if (!termo) return true;
        return [c.rotulo, c.servico_nome, c.prestador_nome, c.exame_id, c.pedido_id, c.servico_rel?.id]
            .filter(Boolean).join(' ').toLowerCase().includes(termo);
    });

    const temFiltro = fStatus || fServico || fPrestador || fDataDe || fDataAte || termo;
    const limparFiltros = () => {
        setFStatus(''); setFServico(''); setFPrestador(''); setFDataDe(''); setFDataAte(''); setBusca('');
    };

    // Agrupa por pedido de exame; ciclos sem pedido caem em "avulsos".
    const grupos = useMemo(() => {
        if (!agrupado) return null;

        const mapa = new Map();
        const avulsos = [];

        visiveis.forEach(c => {
            if (!c.pedido_id) { avulsos.push(c); return; }
            if (!mapa.has(c.pedido_id)) mapa.set(c.pedido_id, { pedido: c.pedido, itens: [] });
            mapa.get(c.pedido_id).itens.push(c);
        });

        const lista = [...mapa.entries()]
            .map(([id, g]) => ({ id, ...g }))
            .sort((a, b) => b.id - a.id);

        return { lista, avulsos };
    }, [agrupado, visiveis]);

    const selecionado = ciclos.find(c => c.key === cicloKey) || null;

    const renderLinha = (c) => {
        const ativo = selecionado && selecionado.key === c.key;
        const token = tokenStatus(c.situacao);
        const podeConfirmar = c.agendamento
            && !['finalizado', 'cancelado', 'arquivo_pendente'].includes(c.situacao)
            && (c.agendamento.status_aceite === 'aceito' || !c.agendamento.envia_aceite);
        const rotuloStatus = rotuloSituacao(c);

        return (
            <div
                key={c.key}
                onClick={() => setCicloKey(c.key)}
                style={{
                    display: 'grid', gridTemplateColumns: GRADE, gap: '16px',
                    padding: '14px 20px', borderBottom: '1px solid #eef2f7', cursor: 'pointer',
                    minWidth: '860px',
                    background: ativo ? '#f2f7fd' : 'transparent',
                    boxShadow: ativo ? 'inset 3px 0 0 #1c5fb0' : 'none',
                }}
            >
                <div className="d-flex flex-column" style={{ gap: '2px' }}>
                    <div style={{ fontSize: '14px', fontWeight: 700, color: c.cancelado ? '#6b7a8d' : '#0f2748' }}>{c.rotulo}</div>
                    <div style={{ fontSize: '12px', color: '#8494a6' }}>{c.servico_nome}</div>
                    {/* fora do modo agrupado, o pedido de origem vira metadado da linha */}
                    {!agrupado && c.pedido_id && (
                        <div style={{ fontSize: '11px', color: '#9aa8b7' }} title={`Item do pedido de exame #${c.pedido_id}`}>
                            pedido #{c.pedido_id}
                        </div>
                    )}
                </div>

                <div className="d-flex align-items-center flex-wrap" style={{ gap: '8px' }}>
                    {c.cancelado ? (
                        <span style={{ fontSize: '12px', color: '#9aa8b7' }}>
                            Ciclo encerrado{c.data_cancelamento ? ` — cancelado em ${formataDataHoraExibicao(c.data_cancelamento)}` : ''}
                        </span>
                    ) : (
                        <>
                            {c.exame_id
                                ? <Elo texto={`Exame #${c.exame_id}`} estado={c.tipo === 'exame' ? 'atual' : 'ok'} />
                                : <Elo texto="Sem exame vinculado" estado="lacuna" />}
                            <span style={{ color: '#b9c4d1', fontSize: '12px' }}>→</span>
                            {c.agendamento_id
                                ? <Elo texto={`Agend. #${c.agendamento_id}`} estado={c.servico_rel ? 'ok' : 'atual'} />
                                : <Elo texto="Sem agendamento" estado="lacuna" />}
                            <span style={{ color: '#b9c4d1', fontSize: '12px' }}>→</span>
                            {c.servico_rel
                                ? <Elo texto={`Serviço #${c.servico_rel.id}`} estado="ok" />
                                : (c.lacuna === 'finalizado_sem_servico'
                                    ? <Elo texto="Serviço não gerado" estado="lacuna" />
                                    : <Elo texto="Serviço" estado="vazio" />)}
                        </>
                    )}
                </div>

                <div className="d-flex flex-column" style={{ gap: '2px' }}>
                    <div style={{ fontSize: '13px', color: c.cancelado ? '#6b7a8d' : '#26364a', fontWeight: 600 }}>
                        {SEM_DATA(c.data) ? '—' : formataDataHoraExibicao(c.data)}
                    </div>
                    <div style={{ fontSize: '12px', color: '#6b7a8d' }}>
                        {c.prestador_nome || (c.tipo === 'exame' ? 'Laboratório não definido' : 'Prestador não atribuído')}
                    </div>
                </div>

                <div style={{ minWidth: 0 }}>
                    <span title={rotuloStatus} style={{ display: 'inline-block', maxWidth: '100%', fontSize: '12px', fontWeight: 600, borderRadius: '4px', padding: '3px 9px', color: token.color, background: token.background, whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
                        {rotuloStatus}
                    </span>
                </div>

                {/* uma ação por linha — sempre a próxima do ciclo, em contorno */}
                <div className="d-flex justify-content-end align-items-start flex-wrap" style={{ gap: '4px', minWidth: 0 }}>
                    {c.lacuna === 'sem_exame' && podeVincularExame(c.agendamento) && (
                        <Button variant="outline-secondary" size="sm" onClick={(e) => { e.stopPropagation(); onVincularExame(c.agendamento); }}>
                            Vincular
                        </Button>
                    )}
                    {c.lacuna === 'exame_sem_agendamento' && podeAgendar && (
                        <Button variant="outline-secondary" size="sm" onClick={(e) => { e.stopPropagation(); onAgendarServico(); }}>
                            Agendar
                        </Button>
                    )}
                    {c.lacuna === 'finalizado_sem_servico' && (['', 'gestor', 'operador'].includes(dadosUsuario['tipo_usuario']) || dadosUsuario['is_superuser']) && (
                        <Button variant="outline-secondary" size="sm" onClick={(e) => { e.stopPropagation(); onGerarServico(c.agendamento); }}>
                            Gerar
                        </Button>
                    )}
                    {!c.lacuna && podeConfirmar && (
                        <Button variant="outline-success" size="sm" onClick={(e) => { e.stopPropagation(); onConfirmarRealizacao(c.agendamento); }}>
                            Realizar
                        </Button>
                    )}
                    <Button variant="outline-secondary" size="sm" title="Detalhes do ciclo" onClick={(e) => { e.stopPropagation(); setCicloKey(c.key); }}>
                        ⋯
                    </Button>
                </div>
            </div>
        );
    };

    const renderCabecalhoGrade = () => (
        <div style={{ display: 'grid', gridTemplateColumns: GRADE, gap: '16px', padding: '10px 20px', borderBottom: '1px solid #e6ecf3', fontSize: '11px', fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: '#8494a6', minWidth: '860px' }}>
            <div>Ciclo</div>
            <div>Exame → Agendamento → Serviço</div>
            <div>Quando / prestador</div>
            <div>Situação</div>
            <div style={{ textAlign: 'right' }}>Ação</div>
        </div>
    );

    return (
        <Card className="cardDestaque mt-4">
            <Card.Body className="p-0">
                <div className="d-flex align-items-center justify-content-between gap-3 flex-wrap" style={{ padding: '16px 20px', borderBottom: '1px solid #e6ecf3' }}>
                    <div className="d-flex align-items-baseline gap-2">
                        <div style={{ fontSize: '17px', fontWeight: 700, color: '#0f2748' }}>Exames e Agendamentos</div>
                        <div style={{ fontSize: '13px', color: '#6b7a8d' }}>
                            {ciclos.length} {ciclos.length === 1 ? 'ciclo' : 'ciclos'} · atendimento #{atendimentoId}
                        </div>
                    </div>
                    <div className="d-flex gap-2">
                        {podeAbrirPedidoExame && (
                            <Button variant="outline-secondary" size="sm" onClick={onNovoPedidoExame}>Novo pedido de exame</Button>
                        )}
                        {podeAgendar && (
                            <Button variant="success" size="sm" onClick={onAgendarServico}>Agendar serviço</Button>
                        )}
                    </div>
                </div>

                {/* O agrupamento por pedido precisa da lista inteira. Quando o atendimento
                    passa do teto, é melhor dizer isso do que exibir uma parte como se fosse tudo. */}
                {totalAgendamentos > agendamentos.length && (
                    <div style={{ padding: '10px 20px', background: '#fdf3dd', borderBottom: '1px solid #f0e0b8', fontSize: '12.5px', color: '#8a6a1f' }}>
                        Este atendimento tem <strong>{totalAgendamentos} agendamentos</strong> e esta aba está mostrando
                        os <strong>{agendamentos.length} mais recentes</strong>. Para o histórico completo, use a aba
                        Agendamentos, que é paginada.
                    </div>
                )}

                <div className="d-flex align-items-center gap-2 flex-wrap" style={{ padding: '12px 20px', background: '#f7fafc', borderBottom: '1px solid #e6ecf3' }}>
                    <span style={{ fontSize: '12px', color: '#6b7a8d', fontWeight: 600, marginRight: '4px' }}>Pendências</span>
                    {filtros.map(f => {
                        const ativo = filtro === f.chave;
                        return (
                            <button
                                key={f.chave}
                                type="button"
                                onClick={() => setFiltro(f.chave)}
                                style={{
                                    border: ativo ? '1px solid #0f2748' : '1px solid #dbe3ec',
                                    background: ativo ? '#0f2748' : '#fff',
                                    color: ativo ? '#fff' : '#26364a',
                                    fontSize: '12px', fontWeight: 600, padding: '5px 11px',
                                    borderRadius: '20px', cursor: 'pointer',
                                }}
                            >
                                {f.rotulo} <span style={{ color: ativo ? 'rgba(255,255,255,.7)' : (f.chave === 'todos' ? '#6b7a8d' : '#b7791f') }}>{f.total}</span>
                            </button>
                        );
                    })}
                    <div style={{ flex: 1 }}></div>
                    <div className="form-check form-switch m-0" style={{ fontSize: '13px', color: '#26364a' }}>
                        <input className="form-check-input" type="checkbox" id="agruparPedido" checked={agrupado} onChange={(e) => setAgrupado(e.target.checked)} style={{ cursor: 'pointer' }} />
                        <label className="form-check-label" htmlFor="agruparPedido" style={{ cursor: 'pointer' }}>Agrupar por pedido</label>
                    </div>
                </div>

                <div className="d-flex align-items-end gap-2 flex-wrap" style={{ padding: '12px 20px', borderBottom: '1px solid #e6ecf3' }}>
                    <div>
                        <label style={ESTILO_ROTULO_FILTRO}>Status</label>
                        <select className="form-select form-select-sm" style={{ ...ESTILO_SELECT_FILTRO, width: '190px' }} value={fStatus} onChange={(e) => setFStatus(e.target.value)}>
                            <option value="">Todos</option>
                            {opcoes.status.map(s => {
                                const exemplo = ciclos.find(c => c.situacao === s);
                                return <option key={s} value={s}>{rotuloSituacao(exemplo)}</option>;
                            })}
                        </select>
                    </div>
                    <div>
                        <label style={ESTILO_ROTULO_FILTRO}>Serviço</label>
                        <select className="form-select form-select-sm" style={{ ...ESTILO_SELECT_FILTRO, width: '220px' }} value={fServico} onChange={(e) => setFServico(e.target.value)}>
                            <option value="">Todos</option>
                            {opcoes.servicos.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                    </div>
                    <div>
                        <label style={ESTILO_ROTULO_FILTRO}>Prestador</label>
                        <select className="form-select form-select-sm" style={{ ...ESTILO_SELECT_FILTRO, width: '200px' }} value={fPrestador} onChange={(e) => setFPrestador(e.target.value)}>
                            <option value="">Todos</option>
                            {opcoes.prestadores.map(p => <option key={p} value={p}>{p}</option>)}
                        </select>
                    </div>
                    <div>
                        <label style={ESTILO_ROTULO_FILTRO}>Data de</label>
                        <input type="date" className="form-control form-control-sm" style={{ ...ESTILO_INPUT_FILTRO, width: '150px' }} value={fDataDe} onChange={(e) => setFDataDe(e.target.value)} />
                    </div>
                    <div>
                        <label style={ESTILO_ROTULO_FILTRO}>até</label>
                        <input type="date" className="form-control form-control-sm" style={{ ...ESTILO_INPUT_FILTRO, width: '150px' }} value={fDataAte} onChange={(e) => setFDataAte(e.target.value)} />
                    </div>
                    <div style={{ flex: 1, minWidth: '160px' }}>
                        <label style={ESTILO_ROTULO_FILTRO}>Buscar</label>
                        <input className="form-control form-control-sm" style={ESTILO_INPUT_FILTRO} value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Serviço, prestador, #id" />
                    </div>
                    {temFiltro && (
                        <Button variant="link" size="sm" className="text-decoration-none px-1" onClick={limparFiltros}>Limpar</Button>
                    )}
                </div>

                <div className="d-flex align-items-stretch flex-wrap">
                    <div style={{ flex: '1 1 640px', minWidth: 0, overflowX: 'auto' }}>
                        {visiveis.length === 0 ? (
                            <div style={{ padding: '24px 20px', fontSize: '13px', color: '#6b7a8d' }}>Nenhum ciclo para este filtro.</div>
                        ) : agrupado ? (
                            <>
                                {grupos.lista.map(g => {
                                    const concluidos = g.itens.filter(i => i.servico_rel || i.situacao === 'finalizado' || i.situacao === 'realizado').length;
                                    return (
                                        // um pedido precisa se ler como um bloco: respiro entre eles e
                                        // borda em volta, senao os grupos ficam colados uns nos outros
                                        <div key={g.id} style={{ margin: '0 16px 18px', border: '1px solid #e6ecf3', borderRadius: '8px', overflow: 'hidden' }}>
                                            <div className="d-flex align-items-center justify-content-between gap-2 flex-wrap" style={{ padding: '12px 20px', background: '#f7fafc', borderBottom: '1px solid #e6ecf3', minWidth: '860px' }}>
                                                <div>
                                                    <div style={{ fontSize: '14px', fontWeight: 700, color: '#0f2748' }}>Pedido de exame #{g.id}</div>
                                                    <div style={{ fontSize: '12px', color: '#6b7a8d' }}>
                                                        {[g.pedido?.dados_medico?.nome, g.pedido?.data_recebimento ? `recebido ${formataDataHoraExibicao(g.pedido.data_recebimento)}` : null]
                                                            .filter(Boolean).join(' · ') || 'Sem dados do pedido'}
                                                    </div>
                                                </div>
                                                <span style={{ fontSize: '12px', fontWeight: 600, color: '#8a6a1f', background: '#fdf3dd', borderRadius: '4px', padding: '4px 10px', whiteSpace: 'nowrap' }}>
                                                    {concluidos} de {g.itens.length} concluídos
                                                </span>
                                            </div>
                                            {renderCabecalhoGrade()}
                                            {g.itens.map(renderLinha)}
                                        </div>
                                    );
                                })}
                                {grupos.avulsos.length > 0 && (
                                    <div style={{ margin: '0 16px 18px', border: '1px solid #e6ecf3', borderRadius: '8px', overflow: 'hidden' }}>
                                        <div className="d-flex align-items-center justify-content-between gap-2" style={{ padding: '12px 20px', background: '#f7fafc', borderBottom: '1px solid #e6ecf3', minWidth: '860px' }}>
                                            <div style={{ fontSize: '14px', fontWeight: 700, color: '#0f2748' }}>Avulsos, sem pedido de exame</div>
                                            <span style={{ fontSize: '12px', fontWeight: 600, color: '#4a5a6b', background: '#eef2f7', borderRadius: '4px', padding: '4px 10px' }}>
                                                {grupos.avulsos.length} {grupos.avulsos.length === 1 ? 'ciclo' : 'ciclos'}
                                            </span>
                                        </div>
                                        {renderCabecalhoGrade()}
                                        {grupos.avulsos.map(renderLinha)}
                                    </div>
                                )}
                            </>
                        ) : (
                            <>
                                {renderCabecalhoGrade()}
                                {visiveis.map(renderLinha)}
                            </>
                        )}
                    </div>

                    {selecionado && (
                        <aside style={{ width: '392px', flex: 'none', borderLeft: '1px solid #e6ecf3', background: '#fbfcfe', padding: '20px' }}>
                            <div className="d-flex align-items-start justify-content-between gap-2 mb-3">
                                <div>
                                    <div style={{ fontSize: '12px', letterSpacing: '.1em', textTransform: 'uppercase', color: '#8494a6', fontWeight: 700 }}>Ciclo selecionado</div>
                                    <div style={{ fontSize: '19px', fontWeight: 700, color: '#0f2748' }}>
                                        {selecionado.agendamento_id ? `Agendamento #${selecionado.agendamento_id}` : `Exame #${selecionado.exame_id}`}
                                    </div>
                                    <div style={{ fontSize: '13px', color: '#4a5a6b' }}>
                                        {selecionado.servico_nome}{selecionado.prestador_nome ? ` · ${selecionado.prestador_nome}` : ''}
                                    </div>
                                </div>
                                <Button variant="link" size="sm" className="p-0 text-muted" onClick={() => setCicloKey(null)}>✕</Button>
                            </div>

                            <div className="d-flex flex-column mb-3">
                                {[
                                    {
                                        cor: selecionado.exame_id ? '#1f9d55' : null,
                                        titulo: selecionado.pedido_id ? `Pedido de exame #${selecionado.pedido_id}` : (selecionado.exame_id ? `Exame #${selecionado.exame_id}` : 'Sem exame vinculado'),
                                        linhas: selecionado.exame_id
                                            ? [
                                                selecionado.pedido?.dados_medico?.nome ? `Dr(a) ${selecionado.pedido.dados_medico.nome}` : null,
                                                selecionado.pedido?.data_recebimento ? `Recebido em ${formataDataHoraExibicao(selecionado.pedido.data_recebimento)}` : null,
                                                `Exame solicitado: ${selecionado.servico_nome}`,
                                              ].filter(Boolean)
                                            : ['O agendamento não está vinculado a nenhum pedido de exame.'],
                                    },
                                    {
                                        cor: selecionado.agendamento_id ? '#4c6ef5' : null,
                                        titulo: selecionado.agendamento_id
                                            ? `Agendamento ${situacoesAgendamento[selecionado.situacao] || selecionado.situacao}`
                                            : 'Sem agendamento',
                                        linhas: selecionado.agendamento
                                            ? [
                                                selecionado.agendamento.data_solicitacao ? `Solicitado em ${formataDataHoraExibicao(selecionado.agendamento.data_solicitacao)}` : null,
                                                selecionado.agendamento.data_liberacao ? `Liberado em ${formataDataHoraExibicao(selecionado.agendamento.data_liberacao)}` : null,
                                                SEM_DATA(selecionado.data) ? null : `Realização prevista ${formataDataHoraExibicao(selecionado.data)}`,
                                              ].filter(Boolean)
                                            : ['Este exame ainda não gerou agendamento.'],
                                    },
                                    {
                                        cor: selecionado.servico_rel ? '#1f9d55' : null,
                                        titulo: selecionado.servico_rel ? `Serviço #${selecionado.servico_rel.id}` : 'Serviço faturável pendente',
                                        linhas: selecionado.servico_rel
                                            ? [selecionado.servico_rel.faturamento_id ? `Faturamento #${selecionado.servico_rel.faturamento_id}` : 'Ainda não faturado']
                                            : ['Gerado ao confirmar a realização do agendamento.'],
                                    },
                                ].map((etapa, i, arr) => (
                                    <div key={i} className="d-flex" style={{ gap: '12px' }}>
                                        <div className="d-flex flex-column align-items-center" style={{ width: '16px' }}>
                                            <div style={{
                                                width: '11px', height: '11px', borderRadius: '50%', marginTop: '4px',
                                                background: etapa.cor || '#fff',
                                                border: etapa.cor ? 'none' : '2px dashed #c9a94f',
                                            }}></div>
                                            {i < arr.length - 1 && <div style={{ flex: 1, width: '2px', background: '#dbe3ec' }}></div>}
                                        </div>
                                        <div style={{ paddingBottom: i < arr.length - 1 ? '16px' : 0 }}>
                                            <div style={{ fontSize: '13px', fontWeight: 700, color: etapa.cor ? '#0f2748' : '#8a6a1f' }}>{etapa.titulo}</div>
                                            {etapa.linhas.map((l, j) => <div key={j} style={{ fontSize: '12px', color: '#6b7a8d' }}>{l}</div>)}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {selecionado.agendamento && (
                                <div className="d-flex flex-column gap-2">
                                    {!['finalizado', 'cancelado', 'arquivo_pendente'].includes(selecionado.situacao)
                                        && (selecionado.agendamento.status_aceite === 'aceito' || !selecionado.agendamento.envia_aceite) && (
                                        <Button variant="success" onClick={() => onConfirmarRealizacao(selecionado.agendamento)}>Confirmar realização</Button>
                                    )}
                                    <div className="d-flex gap-2">
                                        <Button variant="outline-secondary" size="sm" className="flex-fill" onClick={() => onEditarAgendamento(selecionado.agendamento)}>Editar</Button>
                                        {podeVincularExame(selecionado.agendamento) && (
                                            <Button variant="outline-secondary" size="sm" className="flex-fill" onClick={() => onVincularExame(selecionado.agendamento)}>
                                                <FaLink className="me-1" /> Vincular exame
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            )}

                            <div className="mt-3" style={{ background: '#f2f5f9', borderRadius: '6px', padding: '12px 14px', fontSize: '12px', lineHeight: 1.5, color: '#4a5a6b' }}>
                                Vínculos de exame e serviço faturável são visíveis apenas para os perfis internos — o prestador continua vendo somente os próprios agendamentos.
                            </div>
                        </aside>
                    )}
                </div>
            </Card.Body>
        </Card>
    );
};

export default CicloAtendimento;
